import React from "react";

import { Box } from "@mui/material";

import DarkMinimal from "./DarkMinimal";
import LightEditorial from "./LightEditorial";
import BoldBrand from "./BoldBrand";
import {
  AudiogramEditorState,
  AudiogramTemplate,
  Audiogram,
  BackgroundFit,
  TranscriptParagraph,
  FONT_OPTIONS,
} from "../../types";

interface AudiogramRendererProps {
  state: AudiogramEditorState;
  scale?: number;
  currentTime?: number;
  /** Live, normalized (0..1) amplitude samples driven by the playing audio. */
  audioLevels?: number[];
  /** Whether audio is currently playing (drives the live waveform). */
  isPlaying?: boolean;
}

const ACCENT_COLOR_MAP: Record<AudiogramTemplate, string> = {
  "bold-brand": "#4A9EFF",
  "dark-minimal": "#7C6EF5",
  "light-editorial": "#111111",
};

const FONT_SIZE_MAP: Record<AudiogramTemplate, number> = {
  "bold-brand": 15,
  "light-editorial": 17,
  "dark-minimal": 18,
};

const ASPECT_DIMENSIONS: Record<string, { width: number; height: number }> = {
  "9:16": { width: 1080, height: 1920 },
  "1:1": { width: 1080, height: 1080 },
  "16:9": { width: 1920, height: 1080 },
};

export function audiogramToEditorState(
  audiogram: Audiogram,
): AudiogramEditorState {
  const offsetSec = audiogram.start_time / 1000;
  const transcriptParagraphs: TranscriptParagraph[] = (
    audiogram.paragraphs ?? []
  ).map((p) => ({
    id: p.id,
    start: p.start + offsetSec,
    end: p.end + offsetSec,
    text: p.text,
  }));

  return {
    templateId: audiogram.template_id,
    template: audiogram.template,
    quote: audiogram.quote,
    fontFamily: (() => {
      if (audiogram.subtitle_styles?.font) {
        return `'${audiogram.subtitle_styles.font}', sans-serif`;
      }
      return audiogram.template === "light-editorial"
        ? FONT_OPTIONS[0].family
        : FONT_OPTIONS[1].family;
    })(),
    fontSize:
      audiogram.subtitle_styles?.size ??
      FONT_SIZE_MAP[audiogram.template] ??
      18,
    showName: audiogram.show_name || "",
    guestName: audiogram.guest_name,
    accentColor: audiogram.accent_color,
    waveformStyle: audiogram.waveform_style,
    waveformColor: "",
    waveformHeight: (audiogram.waveform_height ?? "medium") as
      | "compact"
      | "medium"
      | "tall",
    animated: audiogram.animated ?? true,
    showWaveform: audiogram.show_waveform ?? true,
    showGuestAvatar: audiogram.show_guest_avatar ?? true,
    aspectRatio: audiogram.aspect_ratio,
    showEpisodeNumber: false,
    showDate: audiogram.show_date ?? true,
    tags: [],
    textColor:
      audiogram.subtitle_styles?.color ??
      (audiogram.template === "light-editorial" ? "#111111" : "#FFFFFF"),
    startTime: audiogram.start_time,
    endTime: audiogram.end_time,
    transcriptParagraphs,
    selectedParagraphIndex: 0,
    date: new Date(audiogram.created_at).toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
    }),
    backgroundImage: audiogram.background_image_url || undefined,
    backgroundImageKey: undefined,
    backgroundOverlay: audiogram.background_overlay ?? 0.5,
    backgroundFit: (audiogram.background_fit as BackgroundFit) ?? "cover",
  };
}

export function getDefaultEditorState(
  template: AudiogramTemplate,
): AudiogramEditorState {
  return {
    templateId: "",
    template,
    quote:
      '"The easiest thing in life is to give up on whatever you\'re focused on."',
    fontFamily:
      template === "light-editorial"
        ? FONT_OPTIONS[0].family
        : FONT_OPTIONS[1].family,
    fontSize: FONT_SIZE_MAP[template] ?? 18,
    showName: "On Purpose",
    guestName: "Lewis Hamilton",
    accentColor: ACCENT_COLOR_MAP[template] ?? "#111111",
    waveformStyle: "bars",
    waveformColor: "",
    waveformHeight: "medium",
    showWaveform: true,
    animated: true,
    aspectRatio: "9:16",
    showEpisodeNumber: false,
    showDate: true,
    textColor: template === "light-editorial" ? "#111111" : "#FFFFFF",
    startTime: 0,
    endTime: 0,
    transcriptParagraphs: [],
    selectedParagraphIndex: 0,
    tags: ["F1", "Success", "Resilience"],
    date: "Feb 20, 2026",
    backgroundOverlay: 0.5,
    backgroundFit: "cover",
    showGuestAvatar: true,
  };
}

function splitIntoSentences(text: string): string[] {
  const raw = text.match(/[^.!?]+[.!?]+[\s]*/g);
  if (!raw || raw.length === 0) return [text];
  return raw.map((s) => s.trim()).filter(Boolean);
}

function getSentenceAtTime(
  paragraph: { start: number; end: number; text: string },
  time: number,
): string {
  const sentences = splitIntoSentences(paragraph.text);
  if (sentences.length <= 1) return paragraph.text;

  const duration = paragraph.end - paragraph.start;
  if (duration <= 0) return paragraph.text;

  const progress = Math.max(
    0,
    Math.min(1, (time - paragraph.start) / duration),
  );
  const idx = Math.min(
    Math.floor(progress * sentences.length),
    sentences.length - 1,
  );
  return sentences[idx];
}

function findClosestParagraph(
  paragraphs: AudiogramEditorState["transcriptParagraphs"],
  time: number,
) {
  let closest = paragraphs[0];
  let minDist = Math.abs(time - closest.start);
  for (let i = 1; i < paragraphs.length; i += 1) {
    const d = Math.min(
      Math.abs(time - paragraphs[i].start),
      Math.abs(time - paragraphs[i].end),
    );
    if (d < minDist) {
      minDist = d;
      closest = paragraphs[i];
    }
  }
  return closest;
}

function getDisplayQuote(
  state: AudiogramEditorState,
  currentTime?: number,
  isPlaying?: boolean,
): string {
  const { transcriptParagraphs: paragraphs } = state;

  // While paused (or when no playhead), show the explicitly selected/edited
  // quote so panel edits and segment selection are reflected immediately.
  // Only follow the transcript by time while audio is actively playing.
  if (currentTime == null || !isPlaying || paragraphs.length === 0) {
    return state.quote;
  }

  const match =
    paragraphs.find((p) => currentTime >= p.start && currentTime <= p.end) ??
    findClosestParagraph(paragraphs, currentTime);

  if (!match) return state.quote;
  return getSentenceAtTime(match, currentTime);
}

function TemplateComponent({
  state,
  scale,
  audioLevels,
  isPlaying,
}: {
  state: AudiogramEditorState;
  scale: number;
  audioLevels?: number[];
  isPlaying?: boolean;
}) {
  const shared = { state, scale, audioLevels, isPlaying };
  switch (state.template) {
    case "light-editorial":
      return <LightEditorial {...shared} />;
    case "bold-brand":
      return <BoldBrand {...shared} />;
    case "dark-minimal":
    default:
      return <DarkMinimal {...shared} />;
  }
}

export default function AudiogramRenderer({
  state,
  scale = 1,
  currentTime,
  audioLevels,
  isPlaying,
}: AudiogramRendererProps) {
  const dims =
    ASPECT_DIMENSIONS[state.aspectRatio] || ASPECT_DIMENSIONS["9:16"];
  const aspectRatio = `${dims.width}/${dims.height}`;

  const displayQuote = getDisplayQuote(state, currentTime, isPlaying);
  const displayState = { ...state, quote: displayQuote };

  return (
    <Box
      sx={{
        width: "100%",
        aspectRatio,
        borderRadius: "12px",
        overflow: "hidden",
        position: "relative",
      }}
    >
      <TemplateComponent
        state={displayState}
        scale={scale}
        audioLevels={audioLevels}
        isPlaying={isPlaying}
      />
    </Box>
  );
}
